import { cx } from '@linaria/core';
import { useRef, useEffect } from 'react';
import { withSuspenseCache, centerCls } from '@pm2/react-utils';
import { pm2SitemapStore, getResourcesForPage, extractResourceGroups } from '@pm2/sitemap-utils';
import { pm2ConfigStore } from '@pm2/store';
import { resets } from '@ui/css';
import { FocusOn, FloatingActionButton, CrossIcon } from '@ui/kit';
import { SizeTransition, CSSRefTransition } from '@utils/react';
import { Resources } from '../../resources/_resource.js';
import { EXPAND_COLLAPSE_DURATION_MS, wrapperCls, closeButtonCls, backdropCls, dropdownCls } from './molecule.resource-dropdown.style.js';
import { jsxs, jsx } from 'react/jsx-runtime';

const ResourceDropdown = ({
  id,
  expanded,
  ready,
  activePage,
  activePageTitle,
  resourceButtonRef,
  onOpen,
  onClose
}) => {
  const buttonRef = useRef(null);

  // Ensure that the suspense promise (which will be used in the
  // <ResourceDropdownContent />) is resolved before attempting to run the
  // transition as this otherwise will trigger a max update size exceeded crash
  const configStore = pm2ConfigStore.useStore();
  const sitemapStore = pm2SitemapStore.useStore();
  withSuspenseCache(getResourcesForPage(configStore, sitemapStore, activePage));
  return /*#__PURE__*/jsxs("div", {
    id: id,
    className: cx(backdropCls, expanded && "active"),
    children: [/*#__PURE__*/jsx(SizeTransition, {
      axis: "vertical",
      in: expanded,
      classNames: wrapperCls,
      timeout: EXPAND_COLLAPSE_DURATION_MS,
      mountOnEnter: true,
      unmountOnExit: true,
      children: ref => /*#__PURE__*/jsx("div", {
        ref: ref,
        className: cx(wrapperCls, resets.rootCls),
        children: /*#__PURE__*/jsx(FocusOn, {
          onEscapeKey: onClose,
          onClickOutside: onClose,
          scrollLock: true,
          focusLock: true,
          allowPinchZoom: true,
          autoFocus: true,
          returnFocus: {
            preventScroll: true
          },
          preventScrollOnFocus: true,
          shards: [buttonRef],
          children: /*#__PURE__*/jsx(ResourceDropdownContent, {
            activePage: activePage,
            activePageTitle: activePageTitle,
            resourceButtonRef: resourceButtonRef,
            onOpen: onOpen,
            onClose: onClose
          })
        })
      })
    }, `content-${ready}`), /*#__PURE__*/jsx(CSSRefTransition, {
      in: expanded,
      classNames: closeButtonCls,
      timeout: EXPAND_COLLAPSE_DURATION_MS,
      mountOnEnter: true,
      unmountOnExit: true,
      children: ref => /*#__PURE__*/jsx("div", {
        ref: ref,
        className: cx(closeButtonCls, expanded && ready && "active"),
        children: /*#__PURE__*/jsx(FloatingActionButton, {
          ref: buttonRef,
          tooltip: {
            placement: "right",
            body: "Luk",
            ariaType: "label"
          },
          onClick: onClose,
          children: /*#__PURE__*/jsx(FloatingActionButton.Icon, {
            children: /*#__PURE__*/jsx(CrossIcon, {
              height: "22.5"
            })
          })
        })
      })
    }, `button-${ready}`)]
  });
};
const ResourceDropdownContent = ({
  activePage,
  onOpen,
  onClose
}) => {
  const configStore = pm2ConfigStore.useStore();
  const sitemapStore = pm2SitemapStore.useStore();
  const resources = withSuspenseCache(getResourcesForPage(configStore, sitemapStore, activePage));

  // once we get here, register that the dropdown has been opened (otherwise it
  // wouldn't be mounted)
  useEffect(() => {
    onOpen();
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);
  return /*#__PURE__*/jsx("aside", {
    tabIndex: -1,
    "data-is-resource-content": true,
    className: cx(dropdownCls, centerCls),
    children: /*#__PURE__*/jsx(Resources, {
      context: "resource-dropdown",
      resourceGroups: extractResourceGroups(resources),
      onLinkClick: onClose
    })
  });
};

export { ResourceDropdown };
//# sourceMappingURL=molecule.resource-dropdown.js.map
